import { createContext, useContext, PropsWithChildren, useMemo } from 'react'
import { useQuery as useQueryBase } from '@apollo/client/react'
import type { ApolloUseQueryResult } from '@vivaldis/graphql'
import {
  EmployeeDetailsQuery,
  EmployeeDetailsQuery_employee,
  EmployeeDetailsQuery_missingCertificates,
  EmployeeDetailsQueryVariables
} from '../graphql/__generated__/EmployeeDetailsQuery'
import { employeeDetailsQuery } from '../graphql/employeeDetailsQuery'
import { useParams } from '../hooks/useParams'

export type QueryContextValue = ApolloUseQueryResult<
  EmployeeDetailsQuery,
  EmployeeDetailsQueryVariables,
  'empty' | 'complete' | 'streaming',
  EmployeeDetailsQueryVariables
>

export const QueryContext = createContext<QueryContextValue | undefined>(
  undefined
)

export function useQuery(): QueryContextValue {
  return useContext(QueryContext)!
}

export function useEmployee(): EmployeeDetailsQuery_employee | null {
  const { data } = useQuery()
  return useMemo(() => {
    return data?.employee || null
  }, [data?.employee])
}

export function useMissingCertificates(): EmployeeDetailsQuery_missingCertificates[] {
  const { data } = useQuery()
  return useMemo(() => {
    return data?.missingCertificates || []
  }, [data?.missingCertificates])
}

export const QueryProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { employeeId } = useParams()
  const queryResult = useQueryBase(employeeDetailsQuery, {
    variables: {
      employeeId: String(employeeId)
    }
  })
  return (
    <QueryContext.Provider value={queryResult}>
      {children}
    </QueryContext.Provider>
  )
}
